<script lang="ts" setup>
  import { meetsNicknameRule, whyNicknameRefused } from '../../../shared/config/nickname.ts';
  import { Button } from '../../../shared/ui/button';
  import { Logo } from '../../../shared/ui/logo';
  import { MobileViewLayout } from '../../../shared/ui/mobile-view-layout';
  import { Spinner } from '../../../shared/ui/spinner';
  import { GameCode } from '../../../widgets/game-code';
  import NicknameInput from './NicknameInput.vue';
  import SongstarLogoHeader from './SongstarLogoHeader.vue';

  defineProps<{
    error: string;
    gameCode?: string;
    hint?: string;
    nicknameError?: string;
    submitText: string;
    submitting?: boolean;
    submittingText?: string;
  }>();
  defineEmits<{ submit: [] }>();
  const nickname = defineModel<string>();
</script>

<template>
  <MobileViewLayout space="m">
    <template #header>
      <SongstarLogoHeader />
    </template>

    <slot>
      <Logo :class="gameCode ? 'top-slot' : 'registration-logo'" />
    </slot>

    <GameCode v-if="gameCode" :game-code="gameCode" class="game-code" />

    <form class="nickname-form" @submit.prevent="!submitting && meetsNicknameRule(nickname) && $emit('submit')">
      <p v-if="error || !hint" class="error-message">{{ error }}</p>
      <p v-else class="error-message hint">{{ hint }}</p>
      <slot name="mode" />
      <slot name="action">
        <NicknameInput
          v-model="nickname"
          :error="nicknameError || whyNicknameRefused(nickname)"
          placeholder="enter nickname (max 15 chars)"
        />
        <div class="submit-action">
          <template v-if="submitting">
            <Spinner />
            <span class="submitting-label">{{ submittingText }}</span>
          </template>
          <Button v-else type="submit" :disabled="!meetsNicknameRule(nickname)">{{ submitText }}</Button>
        </div>
      </slot>
    </form>
  </MobileViewLayout>
</template>

<style scoped>
  .mobile-view-layout {
    --top-slot-height: 5.45rem;
  }

  .registration-logo {
    --logo-size: 3rem;
  }

  .top-slot {
    --logo-size: var(--top-slot-height);
  }

  :slotted(.rejoin-game-card) {
    height: var(--top-slot-height);
  }

  .game-code {
    --game-code-label-margin: 0;
    --game-code-value-size: var(--font-size-xl);

    margin: -0.5rem 0 -1rem;
  }

  .nickname-form {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: var(--control-max-width);
    width: 100%;
  }

  .error-message {
    align-items: flex-end;
    color: var(--color-error);
    display: flex;
    justify-content: center;
    line-height: 1.5;
    margin: 0 0 -0.5rem;
    min-height: 3rem;
    text-align: center;
  }

  .hint {
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .submit-action {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    justify-content: center;
    min-height: 3.5rem;
    width: 100%;
  }

  .submitting-label {
    color: var(--color-primary);
    font-size: var(--font-size-sm);
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
</style>
